import React, { useState, useEffect, useRef } from 'react';
import { RefreshCw, Download, Maximize2, Minimize2 } from 'lucide-react';
import { authenticatedFetch } from '../../utils/api';
import html2canvas from 'html2canvas';
import styles from './DailyQuote.module.css';

const DailyQuote = () => {
    const [quote, setQuote] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [nextAutoFetchTime, setNextAutoFetchTime] = useState(null);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [imageOnlyMode, setImageOnlyMode] = useState(false);
    const quoteRef = useRef(null);

    const fetchQuote = async () => {
        if (nextAutoFetchTime && new Date() < new Date(nextAutoFetchTime)) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const data = await authenticatedFetch('/api/quotes/daily');
            setQuote(data);
            setNextAutoFetchTime(data.next_refresh_time);
            setError(null);
        } catch (err) {
            setError('Unable to retrieve quote data');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = async () => {
        try {
            setRefreshing(true);
            setImageLoaded(false);
            const data = await authenticatedFetch('/api/quotes/refresh', {
                method: 'POST'
            });
            setQuote(data);
            setNextAutoFetchTime(data.next_refresh_time);
            setError(null);
        } catch (err) {
            setError('Quote refresh failed');
            console.error(err);
        } finally {
            setRefreshing(false);
        }
    };

    const handleDownload = async () => {
        if (!quoteRef.current || downloading) return;

        try {
            setDownloading(true);

            // Make sure the image is loaded before capturing
            if (quote?.image_url && !imageLoaded) {
                await new Promise((resolve) => {
                    const img = new Image();
                    img.src = quote.image_url;
                    img.onload = resolve;
                });
                setImageLoaded(true);
            }

            const element = quoteRef.current;
            const elementRect = element.getBoundingClientRect();
            // eslint-disable-next-line
            const aspectRatio = elementRect.width / elementRect.height;

            const canvas = await html2canvas(quoteRef.current, {
                backgroundColor: imageOnlyMode ? 'transparent' : '#000000',
                scale: 2,
                logging: false,
                useCORS: true,
                onclone: (clonedDoc) => {
                    // Get the cloned elements
                    const clonedElement = clonedDoc.querySelector(`.${styles.captureWrapper}`);
                    const clonedImg = clonedDoc.querySelector(`.${styles.backgroundImage} img`);
                    const clonedOverlay = clonedDoc.querySelector(`.${styles.darkOverlay}`);
                    const clonedContent = clonedDoc.querySelector(`.${styles.content}`);

                    // Set explicit dimensions to preserve aspect ratio
                    if (clonedElement) {
                        clonedElement.style.width = `${elementRect.width}px`;
                        clonedElement.style.height = `${elementRect.height}px`;
                    }

                    // Fix background image positioning and object-fit
                    if (clonedImg) {
                        clonedImg.style.opacity = '1';
                        clonedImg.style.width = '100%';
                        clonedImg.style.height = '100%';
                        clonedImg.style.objectFit = 'cover';
                        clonedImg.style.objectPosition = 'center';
                    }

                    // Add the explicit overlay for export
                    if (clonedElement && !imageOnlyMode) {
                        // Make sure the overlay is visible in the clone
                        if (clonedOverlay) {
                            clonedOverlay.style.opacity = '1';
                        }
                    }

                    // Make sure the content is properly styled for export
                    if (clonedContent && !imageOnlyMode) {
                        clonedContent.style.opacity = '1';
                        clonedContent.style.display = 'block';
                        clonedContent.style.zIndex = '10';
                    }

                    // Hide all buttons from the export
                    const actionButtons = clonedDoc.querySelectorAll(`.${styles.actionButton}`);
                    actionButtons.forEach(button => {
                        button.style.display = 'none';
                    });

                    // Prepare the overlay for export
                    const controlsOverlay = clonedDoc.querySelector(`.${styles.imageControls}`);
                    if (controlsOverlay) {
                        controlsOverlay.style.display = 'none';
                    }

                    // Fix the dark overlay
                    const darkOverlay = clonedDoc.querySelector(`.${styles.darkOverlay}`);
                    if (darkOverlay) {
                        darkOverlay.style.position = 'absolute';
                        darkOverlay.style.top = '0';
                        darkOverlay.style.left = '0';
                        darkOverlay.style.width = '100%';
                        darkOverlay.style.height = '100%';
                        darkOverlay.style.backgroundColor = 'rgba(0, 0, 0, 0.6)';
                        darkOverlay.style.zIndex = '2';
                    }
                }
            });

            // Add watermark only if not in image-only mode
            if (!imageOnlyMode) {
                const ctx = canvas.getContext('2d');
                ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
                ctx.font = '12px Inter, sans-serif';
                ctx.fillText('topspeaks.com', canvas.width - 100, canvas.height - 10);
            }

            const link = document.createElement('a');
            link.download = `insight-${new Date().toISOString().split('T')[0]}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
        } catch (err) {
            console.error('Export failed:', err);
        } finally {
            setDownloading(false);
        }
    };

    const toggleImageMode = () => {
        setImageOnlyMode(!imageOnlyMode);
    };

    useEffect(() => {
        const cachedQuote = localStorage.getItem('dailyQuote');
        const cachedNextAutoFetchTime = localStorage.getItem('nextAutoFetchTime');

        if (cachedQuote && cachedNextAutoFetchTime) {
            const parsedQuote = JSON.parse(cachedQuote);
            if (new Date() < new Date(cachedNextAutoFetchTime)) {
                setQuote(parsedQuote);
                setNextAutoFetchTime(cachedNextAutoFetchTime);
                setLoading(false);
                return;
            }
        }

        fetchQuote();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (quote && nextAutoFetchTime) {
            localStorage.setItem('dailyQuote', JSON.stringify(quote));
            localStorage.setItem('nextAutoFetchTime', nextAutoFetchTime);
        }
    }, [quote, nextAutoFetchTime]);

    if (loading) {
        return (
            <div className={styles.container}>
                <div className={styles.loadingState}>
                    <div className={styles.loadingIndicator} />
                    <p className={styles.loadingMessage}>Loading quote...</p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div
                ref={quoteRef}
                className={`${styles.captureWrapper} ${imageOnlyMode ? styles.imageOnlyMode : ''}`}
            >
                {quote?.image_url && (
                    <div className={styles.backgroundImage}>
                        <img
                            src={quote.image_url}
                            alt=""
                            onLoad={() => setImageLoaded(true)}
                            className={`${imageLoaded ? styles.loaded : ''} ${imageOnlyMode ? styles.fullOpacity : ''}`}
                        />
                        {/* Explicit overlay div for better export compatibility */}
                        {!imageOnlyMode && (
                            <div className={styles.darkOverlay}></div>
                        )}
                    </div>
                )}
                <div className={`${styles.content} ${imageOnlyMode ? styles.hidden : ''}`}>
                    <div className={styles.header}>
                        <h2 className={styles.title}>Daily Inspiration</h2>
                        <div className={styles.actions}>
                            <button
                                className={styles.actionButton}
                                onClick={toggleImageMode}
                                aria-label={imageOnlyMode ? "Show quote" : "Show image only"}
                            >
                                {imageOnlyMode ? (
                                    <Minimize2 size={18} className={styles.actionIcon} />
                                ) : (
                                    <Maximize2 size={18} className={styles.actionIcon} />
                                )}
                            </button>
                            <button
                                className={styles.actionButton}
                                onClick={handleRefresh}
                                disabled={refreshing}
                                aria-label="Refresh insight"
                            >
                                <RefreshCw
                                    size={18}
                                    className={`${styles.actionIcon} ${refreshing ? styles.spinning : ''} ${downloading ? styles.hidden : ''}`}
                                />
                            </button>
                            <button
                                className={styles.actionButton}
                                onClick={handleDownload}
                                disabled={downloading}
                                aria-label="Export insight"
                            >
                                <Download
                                    size={18}
                                    className={`${styles.actionIcon} ${downloading ? styles.hidden : ''}`}
                                />
                            </button>
                        </div>
                    </div>
                    <div className={styles.quoteWrapper}>
                        <blockquote className={styles.quote}>
                            <p className={styles.quoteText}>{quote?.quote}</p>
                            <footer className={styles.quoteFooter}>
                                <cite className={styles.quoteAuthor}>
                                    {/*  {quote?.reference} */}
                                    TopSpeaks
                                </cite>
                            </footer>
                        </blockquote>
                    </div>
                </div>
                {/* Controls overlay that remains visible in image-only mode */}
                {imageOnlyMode && (
                    <div className={styles.imageControls}>
                        <button
                            className={styles.actionButton}
                            onClick={toggleImageMode}
                            aria-label="Show quote"
                        >
                            <Minimize2 size={18} className={styles.actionIcon} />
                        </button>
                        <button
                            className={styles.actionButton}
                            onClick={handleDownload}
                            disabled={downloading}
                            aria-label="Export image"
                        >
                            <Download
                                size={18}
                                className={`${styles.actionIcon} ${downloading ? styles.hidden : ''}`}
                            />
                        </button>
                    </div>
                )}
            </div>
            {error && (
                <div className={styles.errorState}>
                    <p className={styles.errorMessage}>{error}</p>
                </div>
            )}
        </div>
    );
};

export default DailyQuote;